import { useFeatureIsOn } from '@growthbook/growthbook-react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { AppFeatures } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import A from 'components/Link/Link';
import { MY } from 'config/fe-routes';
import { useReferralCode } from 'hooks/useReferralCode';
import useUser from 'hooks/useUser';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GTM, GTMEvents } from 'utils/analytics';

// The side menu drawer stays mounted while closed, so the view is logged only once the menu is open,
// and once per page load, comparable with the app's once per My Yaga mount.
const trackedViewUserIds = new Set<number>();

type Props = {
  isOpen: boolean;
  onNavigate?: () => void;
};

/**
 * Refer a friend banner (DEV-2320 reward arm), the web twin of the mobile "My Yaga" banner. Only the reward arm
 * exists on web: the control arm's bare share is app-only (DEV-2324), so control users see no banner here.
 */
const InviteFriendsBanner = ({ isOpen, onNavigate }: Props) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const isRewardVariant = useFeatureIsOn<AppFeatures>('buyer_referral_reward_enabled');
  const { friendReward, inviterReward, isPaused } = useReferralCode({ enabled: isRewardVariant && isOpen });

  const userId = user?.id;
  const isVisible = !!userId && isRewardVariant && !!friendReward && !isPaused;

  useEffect(() => {
    if (!isOpen || !isVisible || trackedViewUserIds.has(userId)) {
      return;
    }

    trackedViewUserIds.add(userId);
    GTM.logEvent(GTMEvents.INVITE_CTA_VIEWED, { screen: 'side_menu', inviter_user_id: userId, arm: 'reward' });
  }, [isOpen, isVisible, userId]);

  if (!isVisible) {
    return null;
  }

  return (
    <A className="no-style invite-friends-banner" data-testid="invite-friends-banner" {...MY.PROMOCODES_INVITE()} onClick={onNavigate}>
      <img src="/images/referral-gift.svg" alt="" className="invite-friends-banner-gift" />
      <span className="invite-friends-banner-text">
        <span className="invite-friends-banner-title">
          {t('pages.invite-friends.referral.headline', { friendReward, inviterReward })
            .split(/,\s+/)
            .map((part, index, parts) => (
              <span key={part} className="invite-friends-banner-nowrap">
                {index < parts.length - 1 ? `${part},` : part}{' '}
              </span>
            ))}
        </span>
        <span className="invite-friends-banner-subtitle">{t('pages.invite-friends.referral.banner-subtitle')}</span>
      </span>
      <ArrowForwardIcon className="invite-friends-banner-arrow" />
    </A>
  );
};

export default InviteFriendsBanner;
